<template>
	<div
		:class="[
			'color-picker',
			errors.length && 'has-error',
			disabled ? 'is-disabled' : ''
		]"
	>
		<Label
			class="color-picker__label"
			:labelText="label"
			:forElementId="`${uid}_ul`"
			v-if="label"
		/>
		<div class="color-picker__content">
			<input
				class="color-picker__input"
				type="text"
				v-on="listeners"
				:value="value"
				@input="updateInput"
				@change="updateInput"
				data-test="inputText"
			/>
			<i class="icon icon-error-solid" />
			<div class="color-picker__selector-container">
				<div class="color-picker__selector">
					<input
						class="color-picker__selector-input"
						type="color"
						v-on="listeners"
						:value="value"
						@input="updateInput"
						@change="updateInput"
						data-test="inputColor"
					/>
				</div>
			</div>
		</div>
		<HelpFeedback
			class="color-picker__help-feedback"
			:helpText="helpText"
			v-if="helpText"
			data-test="helpFeedback"
		/>
		<ErrorFeedback
			class="color-picker__error-feedback"
			:errorMessages="localErrors"
			v-if="localErrors.length > 0"
			data-test="errorFeedback"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/label/label';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';
import ErrorFeedback from '@/components/forms/error-feedback/error-feedback';
import formComponentMixin from '@/mixins/form-component-mixin';

export default {
	name: 'ColorPicker',
	components: {
		Label,
		HelpFeedback,
		ErrorFeedback
	},
	mixins: [formComponentMixin],
	props: {
		label: {
			type: String,
			default: ''
		},
		value: {
			type: String
		},
		helpText: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			localErrors: [...this.errors],
			notValidColorTextError:
				'The format is "#rrggbb" where rr, gg, bb are two-digit hexadecimal numbers'
		};
	},
	computed: {
		listeners() {
			return {
				...this.$listeners,
				input: this.updateInput
			};
		}
	},
	methods: {
		updateInput(e) {
			this.$emit('input', e.target.value);
			this.$emit('change', e.target.value);

			this.updateValidHexaColorError(e.target.value);
		},
		updateValidHexaColorError(color) {
			const errorIndex = this.localErrors.findIndex(
				error => error === this.notValidColorTextError
			);
			if (this.isValidHexaColor(color)) {
				if (errorIndex > -1) {
					this.localErrors.splice(errorIndex, 1);
				}
			} else {
				this.localErrors = new Set(this.localErrors);
				this.localErrors.add(this.notValidColorTextError);
				this.localErrors = Array.from(this.localErrors);
			}
		},
		isValidHexaColor(color) {
			const hexRegex = /^#?([0-9A-Fa-f]{6}|[0-9A-Fa-f]{6})$/;
			return hexRegex.test(color);
		}
	},
	watch: {
		errors(newValue) {
			this.localErrors = [...newValue];
			this.updateValidHexaColorError(this.value);
		}
	}
};
</script>
<style lang="scss">
@import '@/scss/5-objects/_form.scss';

.color-picker {
	display: inline-flex;
	flex-direction: column;

	&__content {
		position: relative;
		display: flex;
		@include textfield;

		&:focus-within,
		&:active {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}

		& .icon {
			position: relative;
			display: none;
			@include horizontal-center-with-transform;
			right: 40px;
			padding: $input-padding-y $input-padding-x;
			font-size: $default-font-size;
			color: $ui-negative;
			cursor: pointer;
		}

		* {
			cursor: pointer;
		}
	}

	&__input {
		@include input-height;
		padding: $input-padding-y 42px $input-padding-y $input-padding-x;
		width: 100%;
		font-size: $input-font-size;
		appearance: none;
		outline: none;
		border: none;
		border-radius: $border-radius;
		text-overflow: ellipsis;
	}

	&__selector-container {
		border-left: $form-input-border;
	}

	&__selector {
		position: relative;
		overflow: hidden;
		margin: $gutter-md;
		width: 27px;
		height: 14px;
		border-radius: $form-border-radius;
	}

	&__selector-input {
		position: absolute;
		bottom: -12px;
		left: -6px;
		width: 40px;
		height: 39px;
	}

	&.has-error {
		.color-picker {
			&__content {
				-o-outline: $focus-outline;
				-moz-outline: $focus-outline;
				outline: $focus-outline;
				outline-offset: $focus-offset;
				outline-color: $ui-negative;

				& .icon {
					display: inline-block;
				}
			}
		}
	}

	&.is-disabled {
		.color-picker {
			opacity: $disabled-opacity;
		}
	}
}
</style>
